import React, { useEffect, useState } from "react";
import Modal from "@/components/chat/Modal";
import api from "@/lib/api";
import { getUsage, resetUsage, fmtBytes } from "@/lib/dataMeter";
import { getInitials, avatarColor } from "@/lib/avatar";
import { Upload, Download, Trash2 } from "lucide-react";

export default function DataUsageDialog({ onClose }) {
  const [u, setU] = useState(getUsage());
  const [chats, setChats] = useState([]);

  useEffect(() => {
    const on = () => setU(getUsage());
    window.addEventListener("rc-data-usage", on);
    api.get("/chats").then(({ data }) => setChats(data)).catch(() => {});
    return () => window.removeEventListener("rc-data-usage", on);
  }, []);

  const chatName = (id) => {
    const c = chats.find((x) => x.id === id);
    return c?.title || c?.peer?.name || "Chat";
  };
  const topChats = Object.entries(u.chats || {})
    .map(([id, v]) => ({ id, total: (v.sent || 0) + (v.recv || 0), ...v }))
    .filter((x) => x.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);
  const max = Math.max(1, ...u.series.map((s) => s.bytes));

  const Card = ({ label, v, testid }) => (
    <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4" data-testid={testid}>
      <div className="text-base sm:text-xl font-heading text-[var(--rc-gold)] whitespace-nowrap">{fmtBytes((v.sent || 0) + (v.recv || 0))}</div>
      <div className="text-xs text-[var(--rc-muted)] mb-2">{label}</div>
      <div className="flex gap-3 text-[11px] text-[var(--rc-muted)]">
        <span className="flex items-center gap-1"><Upload size={11} /> {fmtBytes(v.sent || 0)}</span>
        <span className="flex items-center gap-1"><Download size={11} /> {fmtBytes(v.recv || 0)}</span>
      </div>
    </div>
  );

  return (
    <Modal title="Data usage" onClose={onClose}>
      <div className="space-y-4" data-testid="data-usage-dialog">
        <p className="text-sm text-[var(--rc-muted)]">Estimated mobile data used by photos, voice notes, videos and files.</p>
        <div className="grid grid-cols-3 gap-3">
          <Card label="Today" v={u.today} testid="usage-today" />
          <Card label="This week" v={u.week} testid="usage-week" />
          <Card label="All time" v={u.total} testid="usage-total" />
        </div>

        <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
          <p className="text-xs text-[var(--rc-muted)] mb-2">Usage / day (14d)</p>
          <div className="flex items-end gap-1 h-24">
            {u.series.map((s, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${s.day}: ${fmtBytes(s.bytes)}`}>
                <div className="w-full rounded-t bg-[var(--rc-gold)]/70" style={{ height: `${(s.bytes / max) * 100}%`, minHeight: s.bytes ? 3 : 0 }} />
                {i % 2 === 0 && <span className="text-[8px] text-[var(--rc-muted)]">{s.day}</span>}
              </div>
            ))}
          </div>
        </div>

        {topChats.length > 0 && (
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-sm font-medium mb-3">Top chats</p>
            <div className="space-y-2">
              {topChats.map((c) => (
                <div key={c.id} className="flex items-center gap-3" data-testid={`usage-chat-${c.id}`}>
                  <div className="h-8 w-8 rounded-full grid place-items-center text-[var(--rc-gold)] text-[10px] font-semibold border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(chatName(c.id)) }}>{getInitials(chatName(c.id))}</div>
                  <div className="min-w-0 flex-1 text-sm truncate">{chatName(c.id)}</div>
                  <div className="text-xs text-[var(--rc-gold)]">{fmtBytes(c.total)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={resetUsage} data-testid="reset-usage-btn" className="w-full flex items-center justify-center gap-2 rounded-xl border border-red-500/30 text-red-400 py-2.5">
          <Trash2 size={15} /> Reset statistics
        </button>
      </div>
    </Modal>
  );
}
